import {useState, useCallback, useEffect, useRef, useMemo} from 'react';
import {
  Button,
  Form,
  Text,
  Thumbnail,
  Pagination,
  Key,
  InlineStack,
  BlockStack,
  Box,
} from '@shopify/polaris';
import {useI18n} from '@shopify/react-i18n';
import {TranslatableResourceType} from 'types';
import {useTranslatableResource} from 'features/Translations/TranslatableResource/hooks';
import {useMutation, useQuery} from '@shopify/react-graphql';
import {useMonorail} from 'packages/monorail';
import {useSessionId} from 'packages/@web-utilities/session-id';
import {useToast} from 'utilities/toasts';
import {isShopifyMobile} from '@shopify/app-bridge/utilities';
import {ImageIcon} from '@shopify/polaris-icons';
import {LoadingBar} from 'components/LoadingBar';
import {useMachineTranslate} from 'utilities/use-machine-translate';
import {useSetShowFeedbackCardDate} from 'utilities/use-set-show-feedback-card-date';
import {useShopLocalesAndMarkets} from 'utilities/use-shop-locales-and-markets';
import {useTranslatableResourceForm} from 'features/Translations/hooks';
import {useUrlParams} from 'utilities/use-url-params';
import {useShop} from '@web-utilities/shop';
import {useConfirmLeave} from 'components/ConfirmLeaveModal/use-confirm-leave';
import {SaveBar, useAppBridge} from '@shopify/app-bridge-react';

import {cookieBannerGrouping} from '../../utils/cookieBannerGrouping';
import {FooterHelpLink} from '../FooterHelpLink';
import {
  MISC_CATEGORY,
  isUnsearchableThemeResourceType,
  getThemeAssetCategoryName,
} from '../TranslatableResourceSideBar';

import {
  TranslatableResourceTable,
  UntranslatedNotificationBanner,
  NoBaseTranslationsBanner,
  TranslatableResourceTableSkeleton,
  TitleWrapper,
  RevertNotificationModal,
  revertableResourceType,
} from './components';
import {TranslatableField} from './components/types';
import CreateTranslationsMutation from './graphql/CreateTranslationsMutation.graphql';
import DeleteTranslationsMutation from './graphql/DeleteTranslationsMutation.graphql';
import CreateMarketLocalizationsMutation from './graphql/CreateMarketLocalizationsMutation.graphql';
import DeleteMarketLocalizationsMutation from './graphql/DeleteMarketLocalizationsMutation.graphql';
import styles from './TranslatableResourceForm.scss';
import MachineTranslationsQuery from './graphql/MachineTranslationsQuery.graphql';
import SupportedLanguagesQuery from './graphql/SupportedLanguagesQuery.graphql';
import {
  buildFields,
  buildMutationsFromDirtyFields,
  buildMutationsResults,
} from './utils/utils';
import {useExtractPreviewsData, useFilesPreview} from './hooks';

const FORM_SAVE_BAR_ID = 'translatable-resource-form-save-bar';

const notificationResourceTypes = [TranslatableResourceType.EmailTemplate];
const PAGE_SIZE = 20;
const paginate = (arr: TranslatableField[]) => {
  return arr.reduce((acc, val, i) => {
    const index = Math.floor(i / PAGE_SIZE);
    const page = acc[index] || (acc[index] = []);
    page.push(val);

    return acc;
  }, [] as TranslatableField[][]);
};

export interface Props {
  loading: boolean;
  translatedContentLocaleName: string;
  defaultContentLocaleName: string;
}

export function TranslatableResourceForm({
  loading,
  defaultContentLocaleName,
  translatedContentLocaleName,
}: Props) {
  const [i18n] = useI18n();
  const appBridge = useAppBridge();
  const {currentThemeId, loading: shopDataLoading} = useShop();
  const setShowFeedbackCardDate = useSetShowFeedbackCardDate();
  const {
    resourceType,
    resourceDisplayTitle,
    resourceDisplayImage,
    resourceAdminIdType,
    filteredTranslatableEntries,
    filterTranslatableEntries,
    translatableResourcesQueryLoading,
    refetching,
    resourceInformation,
    translatableEntries,
    refetch: refetchTranslatableResource,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
    resourceId,
  } = useTranslatableResource();
  const {setSave, setDirty, dirty, setSubmitting, submitting} =
    useTranslatableResourceForm();
  const pendingUseEffect = useRef(false);
  const locallyManagedTranslatableEntries = useRef(translatableEntries);
  const {currentLocale, primaryLocale, currentMarket} =
    useShopLocalesAndMarkets();
  const monorail = useMonorail();
  const sessionId = useSessionId();
  const toast = useToast();
  const {urlParams, setUrlParams} = useUrlParams();

  const distinctResourceIds = useMemo(
    () => [...new Set(resourceInformation.map(({resourceId}) => resourceId))],
    [resourceInformation],
  );

  const {resourceIds: resourceIdsForPreview, filenames: filenamesForPreview} =
    useExtractPreviewsData(locallyManagedTranslatableEntries.current);

  const filesPreview = useFilesPreview({
    resourceIds: resourceIdsForPreview,
    filenames: filenamesForPreview.map((filename) => {
      return filename
        .replace('shopify://files/videos/', '')
        .replace('shopify://shop_images/', '');
    }),
  });
  const {
    enabled: machineTranslationEnabled,
    initialized: machineTranslationInitialized,
    ConfirmMachineTranslateModal,
    confirmMachineTranslate,
    inProgressBatch: machineTranslationInProgressBatch,
  } = useMachineTranslate({
    resourceName: resourceDisplayTitle,
    resourceIds: distinctResourceIds,
    skip:
      Boolean(currentMarket) ||
      refetching ||
      !distinctResourceIds.length ||
      resourceType === TranslatableResourceType.ShopPolicy,
  });
  const machineTranslationInProgress = Boolean(
    machineTranslationInProgressBatch &&
      currentLocale &&
      machineTranslationInProgressBatch?.locale === currentLocale?.locale,
  );

  const nonMachineTranslatableResourceTypes = [
    TranslatableResourceType.ShopPolicy,
    TranslatableResourceType.EmailTemplate,
    TranslatableResourceType.PackingSlipTemplate,
  ];
  const allowAutoTranslate =
    machineTranslationEnabled &&
    !currentMarket &&
    currentLocale?.machineTranslatable &&
    !nonMachineTranslatableResourceTypes.includes(resourceType);
  const prevMtInProgress = useRef<boolean>(false);
  const initialFields = useRef<TranslatableField[]>([]);
  const [fields, setFields] = useState<TranslatableField[]>([]);
  const [dirtyFields, setDirtyFields] = useState<TranslatableField[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const isTheme = [
    TranslatableResourceType.OnlineStoreThemeJsonTemplate,
    TranslatableResourceType.OnlineStoreThemeSectionGroup,
    TranslatableResourceType.OnlineStoreThemeSettingsCategory,
    TranslatableResourceType.OnlineStoreThemeLocaleContent,
    TranslatableResourceType.OnlineStoreThemeSettingsDataSections,
    TranslatableResourceType.OnlineStoreThemeAppEmbed,
  ].includes(resourceType);

  const localLoading =
    refetching || !machineTranslationInitialized || filesPreview.loading;

  const {
    confirm: confirmLeave,
    renderConfirmationModal: renderConfirmLeaveModal,
  } = useConfirmLeave(() => Boolean(dirtyFields.length));

  useEffect(() => {
    setDirty(Boolean(dirtyFields.length));
  }, [dirtyFields, setDirty]);

  /**
   * Reset locallyManagedTranslatableEntries when the original ones change
   * in the provider
   */
  useEffect(() => {
    locallyManagedTranslatableEntries.current = translatableEntries;
  }, [translatableEntries]);

  /**
   * Reset form's state when major changes happen in the translatable resource,
   * eg, when the user change page (resourceType, locale, marketId, etc)
   */
  useEffect(() => {
    if (!fields.length) return;

    locallyManagedTranslatableEntries.current = [];
    setFields([]);
    initialFields.current = [];
    setDirtyFields([]);
    pendingUseEffect.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceId, resourceType, currentLocale?.locale, currentMarket?.handle]);

  const pages = useMemo(
    () => (isTheme ? paginate(fields) : [fields]),
    [fields, isTheme],
  );

  useEffect(() => {
    if (!urlParams.highlight) return;

    const pageNumberFromKey = pages.findIndex((page) =>
      page.some((item) => item.fieldKey === urlParams.highlight),
    );

    if (pageNumberFromKey === -1) return;

    setCurrentPage(pageNumberFromKey);
  }, [urlParams.highlight, pages]);

  const fieldsForPage = pages[currentPage] || [];

  const {data: machineTranslationsData, refetch: refetchMachineTranslations} =
    useQuery(MachineTranslationsQuery, {
      variables: {
        resourceIds: distinctResourceIds,
        locale: currentLocale?.locale || '',
      },
      skip:
        !allowAutoTranslate ||
        !machineTranslationInitialized ||
        !currentLocale ||
        !locallyManagedTranslatableEntries.current.length,
    });
  const {data: supportedLanguagesData, loading: supportedLanguagesDataLoading} =
    useQuery(SupportedLanguagesQuery, {
      variables: {
        themeId: currentThemeId,
      },
      skip: shopDataLoading || !revertableResourceType(resourceType),
    });
  const supportedLocales = supportedLanguagesDataLoading
    ? []
    : supportedLanguagesData?.themeLocaleLanguages?.supportedLanguages.map(
        ({code}) => code,
      ) || [];

  const updateTranslatableEntriesWithUpdatedValues = useCallback(() => {
    locallyManagedTranslatableEntries.current =
      locallyManagedTranslatableEntries.current.map((entry) => {
        const translatedContentKey = currentMarket
          ? 'translatedMarketContent'
          : 'translatedContent';

        const fieldToUpdate = initialFields.current.find(
          (field) =>
            field.gidKey === entry.gidKey &&
            field.value !== entry[translatedContentKey]?.value,
        );

        if (!fieldToUpdate) {
          return entry;
        }

        const updatedTranslatedContent = {
          ...entry[translatedContentKey],
          __typename: 'Translation' as const,
          key: entry.key,
          value: fieldToUpdate.value,
          machineTranslated: false,
          updatedAt: new Date().toISOString(),
        };

        return {
          ...entry,
          [translatedContentKey]: updatedTranslatedContent,
        };
      });
  }, [currentMarket]);

  useEffect(() => {
    // Used for the cookie banner fields custom filtering and grouping
    if (resourceType !== TranslatableResourceType.CookieBanner) return;

    filterTranslatableEntries((originalEntries) =>
      originalEntries.filter(({key}) => {
        // The ?. covers the case when we are switching from one type to another and the data hasn't yet
        // loaded. The keys will belong to another type and .section will error:
        const section =
          cookieBannerGrouping[key as keyof typeof cookieBannerGrouping]
            ?.section;

        return section === urlParams.item;
      }),
    );
  }, [filterTranslatableEntries, resourceType, urlParams.item]);

  useEffect(() => {
    if (!isUnsearchableThemeResourceType(resourceType)) return;

    filterTranslatableEntries((originalEntries) =>
      originalEntries.filter(({key}) => {
        return urlParams.item === MISC_CATEGORY
          ? key.split('.').length === 1
          : key.startsWith(`${urlParams.item}.`);
      }),
    );
  }, [filterTranslatableEntries, resourceType, urlParams.item]);

  useEffect(() => {
    /**
     * Do not attempt to build fields if the resource is not ready
     * Especially important to avoid setting pendingUseEffect to false
     *
     * Eg. of lifecycle:
     * 1. User changes resource, type, locale or market
     * 2. Various useEffect get triggered:
     *      - pendingUseEffect is set to true
     *      - fields are emptied
     *      - translatableEntries and filteredTranslatableEntries are emptied
     *        ^ this re-trigger this useEffect
     */
    if (
      !filteredTranslatableEntries.length ||
      !locallyManagedTranslatableEntries.current.length
    )
      return;

    let newFields = buildFields({
      translatableEntries: locallyManagedTranslatableEntries.current,
      filteredTranslatableEntries,
      filesPreview,
      resourceIdsForPreview,
      filenamesForPreview,
      machineTranslations: machineTranslationsData?.machineTranslations,
      currentMarket,
      currentLocale,
      primaryLocale,
      i18n,
    });

    newFields = newFields.map((field) => {
      if (field.groupKey) {
        field.groupSize = newFields.filter(
          (value) => value.groupKey === field.groupKey && !value.hidden,
        ).length;
      }

      return field;
    });

    initialFields.current = [...newFields];
    pendingUseEffect.current = false;

    setFields([...newFields]);
  }, [
    i18n,
    filteredTranslatableEntries,
    machineTranslationsData?.machineTranslations,
    currentMarket,
    currentLocale,
    primaryLocale,
    filesPreview,
    resourceIdsForPreview,
    filenamesForPreview,
  ]);

  useEffect(() => {
    if (prevMtInProgress.current && !machineTranslationInProgress) {
      refetchTranslatableResource();
      refetchMachineTranslations();
    }

    prevMtInProgress.current = machineTranslationInProgress;
  }, [
    machineTranslationInProgress,
    refetchMachineTranslations,
    refetchTranslatableResource,
  ]);

  const lowerCaseResourceType = resourceType.toLowerCase();
  const locale = currentLocale?.locale || '';

  const onSetFields = useCallback(
    (gidKey: string, value: string) => {
      setFields((previousFields) => {
        const targetField = previousFields.find(
          (field) => field.gidKey === gidKey,
        );
        if (!targetField) return previousFields;

        const initialValue =
          initialFields.current.find((field) => field.gidKey === gidKey)
            ?.value || '';

        const {
          value: previousValue,
          error: previousError,
          groupKey,
          reference,
        } = targetField;

        let error: string | undefined;
        if (previousError) {
          // Remove the error if the field is no longer dirty
          error = value === initialValue ? undefined : previousError;
        }

        return previousFields.map((field) => {
          if (
            field.gidKey === gidKey ||
            (field.groupKey &&
              field.groupKey === groupKey &&
              field.reference &&
              reference &&
              field.reference === reference &&
              field.value === previousValue)
          ) {
            return {
              ...field,
              value,
              dirty: value !== initialValue,
              error,
            };
          }

          return field;
        });
      });
    },
    [setFields],
  );

  useEffect(() => {
    // Down below we are assuming fields and initialFields are the same length but
    // because one is a ref and the other is a state that might not be the case.
    if (fields.length !== initialFields.current.length) return;

    const dirtyFields = fields.filter(
      (field, index) => field.value !== initialFields.current[index].value,
    );

    setDirtyFields(dirtyFields);
  }, [fields, initialFields]);

  useEffect(() => {
    if (!isShopifyMobile()) return;
    if (dirty) {
      appBridge.saveBar.show(FORM_SAVE_BAR_ID);
    } else {
      appBridge.saveBar.hide(FORM_SAVE_BAR_ID);
    }
  }, [dirty, appBridge.saveBar]);

  const produceCancelActionMonorailEvent = useCallback(() => {
    monorail.produce({
      schemaId: 'resource_translations_event/1.1',
      payload: {
        eventType: 'discard_edit_translation_click',
        resourceLocale: locale,
        resourceName: resourceDisplayTitle,
        resourceType: lowerCaseResourceType,
        translationUiType: 'side by side',
        sessionToken: sessionId,
      },
    });
  }, [
    locale,
    lowerCaseResourceType,
    monorail,
    resourceDisplayTitle,
    sessionId,
  ]);

  const producePrimaryActionMonorailEvent = useCallback(() => {
    monorail.produce({
      schemaId: 'resource_translations_event/1.1',
      payload: {
        eventType: 'save_edit_translation_click',
        resourceLocale: locale,
        resourceName: resourceDisplayTitle,
        resourceType: lowerCaseResourceType,
        translationUiType: 'side by side',
        sessionToken: sessionId,
      },
    });
  }, [
    locale,
    lowerCaseResourceType,
    monorail,
    resourceDisplayTitle,
    sessionId,
  ]);

  const reset = useCallback(() => {
    if (dirty) produceCancelActionMonorailEvent();
    setFields([...initialFields.current]);
  }, [initialFields, produceCancelActionMonorailEvent, dirty]);

  const createTranslations = useMutation(CreateTranslationsMutation);
  const deleteTranslations = useMutation(DeleteTranslationsMutation);
  const createMarketLocalizations = useMutation(
    CreateMarketLocalizationsMutation,
  );
  const deleteMarketLocalizations = useMutation(
    DeleteMarketLocalizationsMutation,
  );

  const submit = useCallback(async () => {
    setSubmitting(true);
    producePrimaryActionMonorailEvent();
    setShowFeedbackCardDate();

    const {
      createTranslationsMutations,
      deleteTranslationsMutations,
      createMarketLocalizationsMutations,
      deleteMarketLocalizationsMutations,
    } = buildMutationsFromDirtyFields(
      locallyManagedTranslatableEntries.current,
      dirtyFields,
      currentLocale?.locale || '',
      currentMarket,
    );
    const results = await Promise.allSettled([
      ...createTranslationsMutations.map((mutation) =>
        createTranslations({...mutation}),
      ),
      ...deleteTranslationsMutations.map((mutation) =>
        deleteTranslations({...mutation}),
      ),
      ...createMarketLocalizationsMutations.map((mutation) =>
        createMarketLocalizations({...mutation}),
      ),
      ...deleteMarketLocalizationsMutations.map((mutation) =>
        deleteMarketLocalizations({...mutation}),
      ),
    ]);

    const newFields = [...fields];
    const {successfulMutationsValues, failedMutationsFields} =
      buildMutationsResults(
        results,
        createTranslationsMutations,
        createMarketLocalizationsMutations,
        newFields,
      );

    if (Object.keys(failedMutationsFields).length) {
      setSubmitting(false);
      return;
    } else {
      toast.show({
        content: i18n.translate('TranslatableResourceForm.translationSaved', {
          language: currentLocale?.name ?? '',
        }),
        duration: 3000,
      });
    }

    // Update initial fields values with successful mutations values
    dirtyFields
      .filter(
        (fieldBeforeMutation) =>
          !Object.keys(failedMutationsFields).includes(
            fieldBeforeMutation.gidKey,
          ),
      )
      .forEach((fieldBeforeMutation) => {
        const index = initialFields.current.findIndex(
          (el) => el.gidKey === fieldBeforeMutation.gidKey,
        );
        // Fallback to value before mutation if:
        // 1) we ran a delete mutation since the value will always be an empty string
        // 2) the mutation failed
        initialFields.current[index].value =
          successfulMutationsValues[fieldBeforeMutation.gidKey] ||
          fieldBeforeMutation.value;
      });

    setDirty(false);
    setSubmitting(false);
    setDirtyFields([]);
    setFields([...newFields]);
    updateTranslatableEntriesWithUpdatedValues();
  }, [
    createMarketLocalizations,
    createTranslations,
    currentLocale?.locale,
    currentLocale?.name,
    currentMarket,
    deleteMarketLocalizations,
    deleteTranslations,
    dirtyFields,
    fields,
    i18n,
    producePrimaryActionMonorailEvent,
    setShowFeedbackCardDate,
    toast,
    updateTranslatableEntriesWithUpdatedValues,
    setSubmitting,
    setDirty,
  ]);

  useEffect(() => {
    setSave(submit);
  }, [setSave, submit]);

  useEffect(() => {
    setCurrentPage(0);
  }, [urlParams.item]);

  const contextBarMarkup = useMemo(() => {
    if (isShopifyMobile()) {
      const loading = submitting ? '' : undefined;
      return (
        <SaveBar id={FORM_SAVE_BAR_ID}>
          <button
            type="submit"
            variant="primary"
            onClick={submit}
            loading={loading}
          >
            {i18n.translate('Common.save')}
          </button>
          <button type="reset" onClick={reset}>
            {i18n.translate('Common.clear')}
          </button>
        </SaveBar>
      );
    }

    return null;
  }, [reset, submit, submitting, i18n]);

  appBridge.loading(localLoading);

  const formLoading =
    loading ||
    pendingUseEffect.current ||
    translatableResourcesQueryLoading ||
    !fieldsForPage.length;

  const showNotificationBanner =
    !formLoading &&
    notificationResourceTypes.includes(resourceType) &&
    !supportedLocales.includes(currentLocale?.locale || '') &&
    initialFields.current.every(({value}) => value === '');

  const notificationBannerMarkup =
    showNotificationBanner && !currentMarket ? (
      <UntranslatedNotificationBanner />
    ) : null;

  const resourceTypesWithImage = [
    TranslatableResourceType.Collection,
    TranslatableResourceType.OnlineStoreArticle,
    TranslatableResourceType.Product,
  ];

  const autoTranslateButton = useMemo(
    () =>
      allowAutoTranslate ? (
        <Button
          disabled={
            machineTranslationInProgress || !machineTranslationInitialized
          }
          onClick={() =>
            confirmLeave(() => {
              reset();
              confirmMachineTranslate();
            })
          }
          size="slim"
        >
          {i18n.translate('MachineTranslation.autoTranslate')}
        </Button>
      ) : null,
    [
      allowAutoTranslate,
      confirmMachineTranslate,
      i18n,
      machineTranslationInProgress,
      machineTranslationInitialized,
      reset,
      confirmLeave,
    ],
  );

  const machineTranslationInProgressBanner = (
    <LoadingBar
      loading={machineTranslationInProgress}
      text={i18n.translate('MachineTranslation.inProgress.specificResources', {
        language: currentLocale?.name,
      })}
    />
  );

  const getDisplayTitle = (loading: boolean) => {
    const resourceTypeName = i18n.translate(`ResourceTypes.${resourceType}`, {
      count: 1,
    });
    if (loading) {
      return resourceTypeName;
    }

    if (resourceType === TranslatableResourceType.Shop) {
      return i18n.translate('TranslatableResourceForm.metaContent');
    }

    if (
      resourceType === TranslatableResourceType.OnlineStoreThemeLocaleContent &&
      urlParams.item
    ) {
      return `${resourceDisplayTitle} - ${getThemeAssetCategoryName(
        urlParams.item,
      )}`;
    }

    return resourceDisplayTitle || resourceTypeName;
  };

  const title = (
    <Box padding={{xs: '400', sm: '0'}} paddingBlockEnd="0">
      <InlineStack wrap={false} align="start" blockAlign="center" gap="300">
        {resourceTypesWithImage.includes(resourceType) ? (
          <Thumbnail
            source={resourceDisplayImage?.url || ImageIcon}
            alt={resourceDisplayTitle}
            size="small"
          />
        ) : null}
        <TitleWrapper
          resourceId={resourceId}
          resourceType={resourceType}
          resourceAdminIdType={resourceAdminIdType}
          loading={formLoading}
        >
          <Text variant="headingLg" as="h1">
            {getDisplayTitle(formLoading)}
          </Text>
        </TitleWrapper>
      </InlineStack>
    </Box>
  );

  const noBaseTranslations = (() => {
    if (
      !formLoading &&
      !localLoading &&
      currentMarket &&
      fields.length &&
      resourceDisplayTitle
    ) {
      if (isTheme) {
        return fields
          .filter(
            ({isContextualizedThemeContent}) => !isContextualizedThemeContent,
          )
          .every(({reference}) => !reference);
      }

      return fields.every(({reference}) => !reference);
    }

    return false;
  })();

  const noBaseTranslationsBanner = noBaseTranslations ? (
    <NoBaseTranslationsBanner />
  ) : null;

  let pagination;
  if (isTheme && pages.length > 1) {
    pagination = (
      <div className={styles.PaginationContainer}>
        <Pagination
          nextKeys={[Key.RightArrow]}
          previousKeys={[Key.LeftArrow]}
          hasPrevious={currentPage !== 0}
          onPrevious={() =>
            confirmLeave(() => {
              setUrlParams({highlight: undefined});
              reset();
              setCurrentPage(currentPage - 1);
            })
          }
          hasNext={currentPage !== pages.length - 1}
          onNext={() =>
            confirmLeave(() => {
              setUrlParams({highlight: undefined});
              reset();
              setCurrentPage(currentPage + 1);
            })
          }
          label={i18n.translate('TranslatableResourceForm.pageCount', {
            currentPageCount: currentPage + 1,
            maxPageCount: pages.length,
          })}
        />
      </div>
    );
  } else if (hasNextPage || hasPreviousPage) {
    pagination = (
      <div className={styles.PaginationContainer}>
        <Pagination
          nextKeys={[Key.RightArrow]}
          previousKeys={[Key.LeftArrow]}
          hasPrevious={hasPreviousPage}
          onPrevious={() =>
            confirmLeave(() => {
              reset();
              fetchPreviousPage();
            })
          }
          hasNext={hasNextPage}
          onNext={() =>
            confirmLeave(() => {
              reset();
              fetchNextPage();
            })
          }
        />
      </div>
    );
  }

  const table = formLoading ? (
    <TranslatableResourceTableSkeleton resourceType={resourceType} />
  ) : (
    <TranslatableResourceTable
      resourceType={resourceType}
      defaultContentLocaleName={defaultContentLocaleName}
      translatedContentLocaleName={translatedContentLocaleName}
      fields={fieldsForPage}
      setField={onSetFields}
      disabled={
        submitting ||
        refetching ||
        machineTranslationInProgress ||
        !machineTranslationInitialized
      }
      machineTranslationInProgress={machineTranslationInProgress}
      market={currentMarket}
      locale={currentLocale}
      primaryLocale={primaryLocale}
    />
  );

  const footerHelpLink = formLoading ? null : (
    <div className={styles.DesktopFooterHelpWrapper}>
      <FooterHelpLink />
    </div>
  );

  return (
    <BlockStack gap="400">
      {contextBarMarkup}
      <div className={styles.Header}>
        <InlineStack align="space-between" blockAlign="center" gap="300">
          {title}
          <InlineStack blockAlign="center" gap="300">
            {autoTranslateButton}
            <RevertNotificationModal
              resourceId={resourceId!}
              resourceType={resourceType}
              currentMarket={currentMarket}
              currentLocale={currentLocale}
              isSupportedLocale={supportedLocales.includes(
                currentLocale?.locale || '',
              )}
              isLoading={localLoading || formLoading || submitting}
              submitting={submitting}
              refetchTranslatableResource={refetchTranslatableResource}
            />
          </InlineStack>
        </InlineStack>
      </div>
      <Form onSubmit={submit}>
        <BlockStack gap="400">
          {notificationBannerMarkup}
          {noBaseTranslationsBanner}
          {machineTranslationInProgressBanner}
          {table}
        </BlockStack>
      </Form>
      <ConfirmMachineTranslateModal />
      {renderConfirmLeaveModal()}
      {pagination ? (
        <div className={styles.PaginationContainer}>{pagination}</div>
      ) : null}
      {footerHelpLink}
    </BlockStack>
  );
}
